// Adapted from lora-scan apps/frontend/lib/hooks/useScanSession.ts (the plan as slots, what is still missing).
// FaceLock Scan: the set being built, bucket by bucket. Pure and immutable:
// every change returns a new set, so React state and the saved copy
// (store.ts) never disagree.
//
// A frame joins its bucket only when it is not a near-duplicate of any frame
// already held (anywhere in the set; the full-body views, different by
// construction, are not compared with each other) and sits far enough in time
// from the bucket's other frames (spread over time). A full bucket still swaps
// its weakest frame for a clearly sharper one: the sharpest frame wins.

import {
  NEAR_DUPLICATE, SCAN_BUCKETS, SCAN_CLIPS, basePlan, bucketById, cleanPlan, coverage, isNearDuplicate,
  type Coverage, type FrameSignature, type ScanBucketId, type ScanClip, type ScanPlan,
} from "@/shared/face-qc/index.mjs";

/** How a frame was judged: the full live check, the light check (no face model), or an uploaded photo. */
export type FrameCheck = "full" | "basic" | "upload";

export interface ScanFrame extends FrameSignature {
  id: string;
  bucket: ScanBucketId;
  blob: Blob;
  width: number;
  height: number;
  t: number;
  sharpness: number;
  check: FrameCheck;
  /** Rigid face proportions (geometry.faceDescriptor), when a face was measured. */
  descriptor?: number[] | null;
  /** A body picture: the sign of the body model's shoulder order when it was kept (buckets.ts shoulderSignOf). The full-body FRONT pictures' sign is what the back is told from. */
  shoulderSign?: 1 | -1 | null;
  /** Set when the server's face check turned this frame down; the bucket must be retaken. */
  serverReason?: string | null;
}

export type ScanMode = "scan" | "upload";

export interface ScanSet {
  mode: ScanMode;
  frames: ScanFrame[];
  /** Pitch of the first front frames (chin up / down are measured from it). */
  baselinePitch: number | null;
  /** Rigid face proportions of the first front frames (the advisory same-person check). */
  baselineDescriptor: number[] | null;
  /** This scan's own plan (pictures per bucket). None = the base plan, today's 24. Never read in upload mode. */
  plan?: ScanPlan;
}

export const emptySet = (mode: ScanMode = "scan"): ScanSet => ({ mode, frames: [], baselinePitch: null, baselineDescriptor: null });

/** A clearly sharper frame (by this share) replaces a full bucket's weakest one. */
export const REPLACE_GAIN = 1.2;

export const framesOf = (set: ScanSet, bucket: ScanBucketId) => set.frames.filter((f) => f.bucket === bucket);

export function countsOf(set: ScanSet): Record<string, number> {
  const out: Record<string, number> = {};
  for (const f of set.frames) if (!f.serverReason) out[f.bucket] = (out[f.bucket] || 0) + 1;
  return out;
}

/** The plan this scan fills: its own, cleaned, or the base plan (24). */
export const planOf = (set: ScanSet): ScanPlan => (set.plan ? cleanPlan(set.plan) : basePlan());

/** The set with this plan (cleaned: base views can never be planned away). */
export const withPlan = (set: ScanSet, plan: unknown): ScanSet => ({ ...set, plan: cleanPlan(plan) });

/** Pictures a bucket holds in this set: the plan's count for a scan, the checklist's for uploaded photos. */
export function capOf(set: ScanSet, id: ScanBucketId): number {
  if (set.mode === "upload") return bucketById(id)?.count ?? 0;
  return planOf(set).counts[id] ?? 0;
}

export const coverageOf = (set: ScanSet): Coverage => coverage(countsOf(set), set.mode, planOf(set));

/**
 * The shoulder order this scan's full-body FRONT pictures were kept with (the
 * sign the body model gave: buckets.ts FACING): the back is that order
 * flipped. null = no front picture carries one yet (or two disagree): the
 * model's own convention is used. Read from the frames, so a front that is
 * redone or retaken takes its reference with it.
 */
export function frontShoulderSign(set: ScanSet): 1 | -1 | null {
  let sum = 0;
  for (const f of set.frames) if (f.bucket === "full_front" && !f.serverReason && (f.shoulderSign === 1 || f.shoulderSign === -1)) sum += f.shoulderSign;
  return sum > 0 ? 1 : sum < 0 ? -1 : null;
}

/** Buckets still short of what this mode needs, in capture order. */
export function openBuckets(set: ScanSet): ScanBucketId[] {
  const counts = countsOf(set);
  const plan = planOf(set).counts;
  return SCAN_BUCKETS.filter((b) => (counts[b.id] || 0) < (set.mode === "upload" ? 1 : plan[b.id] ?? 0)).map((b) => b.id);
}

/** The next part (face, upper, full) with something still open after `from`, then from the start (a redo can reopen an earlier part). */
export function nextOpenClip(set: ScanSet, from: ScanClip | null): ScanClip | null {
  const open = new Set(openBuckets(set).map((id) => bucketById(id)!.clip));
  const start = from ? SCAN_CLIPS.indexOf(from) + 1 : 0;
  const order = [...SCAN_CLIPS.slice(start), ...SCAN_CLIPS.slice(0, start)];
  return order.find((c) => open.has(c)) ?? null;
}

/** Buckets that could still take one more frame (upload mode fills second slots too). */
export function roomIn(set: ScanSet, bucket: ScanBucketId): number {
  if (!bucketById(bucket)) return 0;
  return Math.max(0, capOf(set, bucket) - framesOf(set, bucket).filter((f) => !f.serverReason).length);
}

export type OfferResult = { set: ScanSet; outcome: "added" | "replaced" | "duplicate" | "tooSoon" | "full"; replaced?: ScanFrame };

/** Offer a frame to its bucket. */
export function offerFrame(set: ScanSet, frame: ScanFrame): OfferResult {
  const b = bucketById(frame.bucket);
  const cap = b ? capOf(set, frame.bucket) : 0;
  if (!b || cap <= 0) return { set, outcome: "full" };
  const same = framesOf(set, frame.bucket).filter((f) => !f.serverReason);
  // The full-body buckets are different views by construction (the front, the two sides and the back, told apart by
  // the body model): they are never compared with each other. A plain outfit against a plain wall makes their tiny
  // thumbnails all but identical, and the back (or the second side) must not be refused as "the same picture" as the
  // front, which would ask the person to "move a little" for ever.
  const otherView = (f: ScanFrame) => b.clip === "full" && bucketById(f.bucket)?.clip === "full";
  const others = set.frames.filter((f) => f.bucket !== frame.bucket && !f.serverReason && !otherView(f));
  if (others.some((o) => isNearDuplicate(o, frame))) return { set, outcome: "duplicate" };
  const dropped = set.frames.filter((f) => f.bucket === frame.bucket && f.serverReason);
  const base = dropped.length ? { ...set, frames: set.frames.filter((f) => !dropped.includes(f)) } : set;
  if (same.length < cap) {
    if (set.mode === "scan" && same.some((s) => Math.abs(s.t - frame.t) < NEAR_DUPLICATE.minGapMs)) return { set, outcome: "tooSoon" };
    if (same.some((s) => isNearDuplicate(s, frame))) return { set, outcome: "duplicate" };
    return { set: withBaseline({ ...base, frames: [...base.frames, frame] }, frame), outcome: "added" };
  }
  // Full: swap the weakest for a clearly sharper, still-distinct frame.
  const weakest = same.reduce((w, f) => (f.sharpness < w.sharpness ? f : w), same[0]);
  if (!(frame.sharpness >= weakest.sharpness * REPLACE_GAIN)) return { set, outcome: "full" };
  const rest = same.filter((f) => f !== weakest);
  if (rest.some((s) => isNearDuplicate(s, frame) || (set.mode === "scan" && Math.abs(s.t - frame.t) < NEAR_DUPLICATE.minGapMs))) return { set, outcome: "full" };
  return { set: withBaseline({ ...base, frames: base.frames.map((f) => (f === weakest ? frame : f)) }, frame), outcome: "replaced", replaced: weakest };
}

/** The front frames set where "level" is and whose face proportions later frames are compared with. */
function withBaseline(set: ScanSet, frame: ScanFrame): ScanSet {
  if (frame.bucket !== "face_front") return set;
  const fronts = set.frames.filter((f) => f.bucket === "face_front" && typeof f.pitch === "number");
  const pitches = fronts.map((f) => f.pitch as number).sort((a, b) => a - b);
  const baselinePitch = pitches.length ? pitches[Math.floor(pitches.length / 2)] : set.baselinePitch;
  const baselineDescriptor = set.baselineDescriptor ?? (frame.descriptor ?? null);
  return { ...set, baselinePitch, baselineDescriptor };
}

/** Take one frame out (the review's Retake). */
export function removeFrame(set: ScanSet, id: string): ScanSet {
  const frames = set.frames.filter((f) => f.id !== id);
  return resetBaselineIfFrontGone({ ...set, frames });
}

/** Empty a bucket (redo it). */
export function clearBucket(set: ScanSet, bucket: ScanBucketId): ScanSet {
  return resetBaselineIfFrontGone({ ...set, frames: set.frames.filter((f) => f.bucket !== bucket) });
}

function resetBaselineIfFrontGone(set: ScanSet): ScanSet {
  if (set.frames.some((f) => f.bucket === "face_front")) return set;
  return { ...set, baselinePitch: null, baselineDescriptor: null };
}

/** Mark frames the server turned down (by id), with its reason: their buckets reopen. */
export function markServerRejects(set: ScanSet, rejects: Record<string, string>): ScanSet {
  return { ...set, frames: set.frames.map((f) => (rejects[f.id] ? { ...f, serverReason: rejects[f.id] } : f)) };
}

/** The set in checklist order (bucket plan order, then time): what is sent. */
export function orderedFrames(set: ScanSet): ScanFrame[] {
  const rank = new Map(SCAN_BUCKETS.map((b, i) => [b.id, i]));
  return set.frames.filter((f) => !f.serverReason).sort((a, b) => (rank.get(a.bucket)! - rank.get(b.bucket)!) || a.t - b.t);
}

/**
 * The base pictures of the set, in the order orderedFrames sends them: each
 * bucket cut to its checklist count, the earliest first. What the face check
 * and the trainer receive whatever the plan (B4 wires it).
 */
export function baseFrames(set: ScanSet): ScanFrame[] {
  const held: Record<string, number> = {};
  return orderedFrames(set).filter((f) => {
    held[f.bucket] = (held[f.bucket] || 0) + 1;
    return held[f.bucket] <= (bucketById(f.bucket)?.count ?? 0);
  });
}

/** Pictures the set holds over its base pictures. */
export const extraCount = (set: ScanSet): number => orderedFrames(set).length - baseFrames(set).length;

/** The set cut back to its base pictures, with no plan of its own. Frames the server turned down stay as they are. */
export function trimToBase(set: ScanSet): ScanSet {
  const keep = new Set(baseFrames(set).map((f) => f.id));
  const out: ScanSet = { ...set, frames: set.frames.filter((f) => f.serverReason || keep.has(f.id)) };
  delete out.plan;
  return out;
}

/** The frames as files for the upload helpers: facelock-scan-<nn>-<bucket>.jpg. */
export function framesToFiles(set: ScanSet): File[] {
  return orderedFrames(set).map((f, i) => new File([f.blob], `facelock-scan-${String(i + 1).padStart(2, "0")}-${f.bucket}.jpg`, { type: "image/jpeg", lastModified: f.t }));
}

let seq = 0;
export function newFrameId(): string {
  seq += 1;
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch { /* fall through */ }
  return `f${Date.now().toString(36)}${seq}`;
}
